import { Popover as PopoverPrimitive, Tooltip as TooltipPrimitive } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { useFloatingLayer } from '../lib/layer.ts';

/** Uygulama kökünde bir kez: araç ipuçlarının gecikmesi ve ardışık gösterim. */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <TooltipPrimitive.Provider delayDuration={300} skipDelayDuration={200}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

type TooltipProps = {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
};

/**
 * Araç ipucu (08 §6 #34): üzerine gelinebilir ve `Esc` ile kapanır (WCAG 1.4.13). Tek başına bilgi taşımaz;
 * tetikleyicinin erişilebilir adı ayrıca verilir.
 */
export function Tooltip({ content, children, side = 'top' }: TooltipProps) {
  const layer = useFloatingLayer();
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className={cn(layer, 'max-w-xs rounded-md bg-fg px-2 py-1 text-caption text-surface shadow-md')}
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-fg" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverClose = PopoverPrimitive.Close;

export function PopoverContent({ className, align = 'start', sideOffset = 6, ...props }: ComponentProps<typeof PopoverPrimitive.Content>) {
  const layer = useFloatingLayer();
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        className={cn(
          layer,
          'w-72 rounded-lg border bg-surface p-4 text-body text-fg shadow-md outline-none dark:border-strong',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

/** Klavye kısayolu gösterimi. */
export function Kbd({ className, ...props }: ComponentProps<'kbd'>) {
  return (
    <kbd
      className={cn(
        'inline-flex min-w-5 items-center justify-center rounded-sm border border-strong bg-surface-muted px-1 font-mono text-caption text-fg-muted',
        className,
      )}
      {...props}
    />
  );
}
